import { useState } from "react"
import { useHistory } from "react-router-dom"
import { Trans } from "@lingui/macro"
import {
  useMutationDeleteKnowledge,
  useMutationCopyKnowledge,
  useMutationDeleteCategory,
  KNOWLEDGE_TYPE,
  useMutationCreateKnowledge
} from "../data"
import { toId, useKnowledgeUtils, mapToIds } from "."
import { useSnackbar } from "../components/SnackbarProvider"

export const useCategory = () => {
  const history = useHistory()

  const [deleteKnowledge] = useMutationDeleteKnowledge()
  const [copyKnowledge] = useMutationCopyKnowledge()
  const [deleteCategory] = useMutationDeleteCategory()
  const { getNavigateToCategoryForID, getNavigateToArticleLinkForID } = useKnowledgeUtils()
  const [createKnowledge] = useMutationCreateKnowledge()
  const { showMessage } = useSnackbar()

  const [currentItemsCount, setCurrentItemsCount] = useState(0)

  const handleCreateArticleCreated = (item) => {
    setCurrentItemsCount((prevCount) => prevCount + 1)
    history.push(`${getNavigateToArticleLinkForID(item.id)}?new=true`)
  }

  const handleEditArticle = (knowledge) => history.push(`/knowledge/article/${toId(knowledge)}/edit`)

  const handleDeleteArticle = async ({ id: deleteId, category }) => {
    const result = await deleteKnowledge({ variables: { id: deleteId } })
    if (result?.data?.deleteKnowledge) {
      setCurrentItemsCount((prevCount) => Math.max(0, prevCount - 1))
      history.push(getNavigateToCategoryForID(toId(category)))
      return result
    }
  }

  const handleCopyArticle = async (knowledge) => {
    const result = await copyKnowledge({ variables: { id: knowledge.id } })
    if (result?.data) {
      history.push(`/knowledge/article/${result?.data?.knowledge.copy.id}?new=true`)
    }
  }

  const handleEditCategory = (category) => history.push(`/knowledge/category/${toId(category)}/edit`)

  const handleDeleteCategory = async (currentCategory) => {
    if (currentCategory?.subCategories?.length) {
      showMessage({
        message: <Trans>This category contains subcategories, these must be deleted or moved first</Trans>
      })
      return
    }

    if (currentItemsCount > 0 || currentCategory?.items?.count > 0) {
      showMessage({
        message: <Trans>This category contains items, these must be deleted or moved first</Trans>
      })
      return
    }

    try {
      await deleteCategory({ variables: { id: toId(currentCategory) } })

      const destination = currentCategory?.parent?.id
        ? `/knowledge/category/${toId(currentCategory.parent)}`
        : "/knowledge"

      showMessage({ message: <Trans>Category deleted successfully</Trans> })
      setTimeout(() => {
        history.push(destination)
      }, 500)
    } catch (error) {
      showMessage({ message: <Trans>Failed to delete category</Trans> })
    }
  }

  const handleCategoryClick = (category) => {
    history.push(getNavigateToCategoryForID(toId(category)))
  }

  const handleCategoryUpload = async (files, currentCategory) => {
    try {
      const uploadResults = await Promise.all(
        files.map((file) =>
          createKnowledge({
            variables: {
              input: {
                type: KNOWLEDGE_TYPE.FILE,
                file: file.id,
                category: toId(currentCategory),
                title: file.fileName.replace(/\.[^/.]+$/, ""),
                content: [],
                locations: currentCategory.locations?.length ? mapToIds(currentCategory.locations) : [],
                groups: currentCategory.groups?.length ? mapToIds(currentCategory.groups) : []
              }
            }
          })
        )
      )

      // Update the count based on successful uploads
      const successfulUploads = uploadResults.filter((result) => result?.data).length

      if (successfulUploads > 0) {
        setCurrentItemsCount((prevCount) => prevCount + successfulUploads)
      }
    } catch (error) {
      console.error("Error creating knowledge files:", error)
    }
  }

  return {
    handleCreateArticleCreated,
    handleEditArticle,
    handleDeleteArticle,
    handleCopyArticle,
    handleEditCategory,
    handleDeleteCategory,
    handleCategoryClick,
    handleCategoryUpload,
    currentItemsCount,
    setCurrentItemsCount
  }
}
